// Slice 31 — MySQL rename emitter.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 31.
//
// Emits dialect-native ALTER syntax for body rewrites + table/view rename.
// MySQL has narrow body-rewrite ALTER coverage:
//   - Views support `ALTER VIEW … AS <newSelect>`.
//   - Procedures / functions / triggers have NO `ALTER … AS …` form — they
//     require DROP + CREATE. The introspection feed delivers the rewritten
//     CREATE-statement text via `newBody` for these kinds, so the emitter
//     emits `DROP … IF EXISTS sch.name; <newBody>` (DDL is non-transactional
//     in MySQL — the executor handles partial-failure undo).
//   - DEFAULT constraints → `ALTER TABLE sch.t ALTER COLUMN col SET DEFAULT
//     <newExpr>` (8.0+). The synthesized `<table>.<column>` name carries the
//     parent table.
//   - CHECK constraints → DROP + ADD wrapped in a multi-statement script that
//     resolves the parent table via INFORMATION_SCHEMA.TABLE_CONSTRAINTS at
//     runtime (constraint-name-only metadata; same shape as PG / MSSQL's
//     dynamic-SQL emitters but using MySQL's PREPARE/EXECUTE primitives).
//
// Quoting: backticks by default; double quotes when ANSI_QUOTES mode is in
// effect. Pass `{ ansiQuotes: true }` to any emitter or `makeMysqlRenameEmitter`
// to get the alternate quote style.

import { quoteMysqlText } from '../../../../core/utils/mysqlSqlLiteral';
import type { ObjectKind, QualifiedName } from '../../types';

import {
  unrewritableRenameSql,
  type DialectRenameEmitter,
  type EmitObjectBodyAlterInput,
} from './types';

const DEFAULT_SCHEMA = '';

export type MysqlRenameEmitterOptions = {
  /** When true, emit identifiers wrapped in `"…"` (ANSI_QUOTES mode).
   *  When false (default), emit `` `…` ``. */
  readonly ansiQuotes?: boolean;
};

type Quoter = (name: string) => string;

const quoteBacktick: Quoter = (name) => '`' + name.replace(/`/g, '``') + '`';
const quoteAnsi: Quoter = (name) => '"' + name.replace(/"/g, '""') + '"';

function pickQuoter(opts?: MysqlRenameEmitterOptions): Quoter {
  return opts?.ansiQuotes ? quoteAnsi : quoteBacktick;
}

function schemaOf(qn: QualifiedName): string {
  return qn.schema && qn.schema.length > 0 ? qn.schema : DEFAULT_SCHEMA;
}

function quotedQualified(qn: QualifiedName, q: Quoter): string {
  const schema = schemaOf(qn);
  return schema ? `${q(schema)}.${q(qn.name)}` : q(qn.name);
}

/** Strip a leading `CREATE OR REPLACE` (rare in MySQL) back to bare `CREATE`
 *  so the DROP + CREATE shape produced for procs / functions / triggers is
 *  consistent. Called at the top of each module-kind emit. */
function normaliseCreate(body: string): string {
  return body.replace(/^\s*create\s+or\s+replace\s+/i, 'CREATE ');
}

function emitViewBodyAlter(qn: QualifiedName, newBody: string, q: Quoter): string {
  // VIEW_DEFINITION returns the bare SELECT — re-wrap in ALTER VIEW.
  // Trailing `;` is stripped so the result has no double terminator.
  const inner = newBody.replace(/;\s*$/, '').trim();
  return `ALTER VIEW ${quotedQualified(qn, q)} AS ${inner}`;
}

function emitDropCreateModule(
  qn: QualifiedName,
  newBody: string,
  q: Quoter,
  keyword: 'PROCEDURE' | 'FUNCTION' | 'TRIGGER',
): string {
  const create = normaliseCreate(newBody).trim();
  return `DROP ${keyword} IF EXISTS ${quotedQualified(qn, q)};\n${create}`;
}

function emitDefaultConstraintAlter(
  qn: QualifiedName,
  newExpr: string,
  q: Quoter,
): string {
  // Synthesized name is `<table>.<column>` — split on the LAST dot.
  const dot = qn.name.lastIndexOf('.');
  if (dot < 0) {
    return `-- TODO(rename emit): default-constraint name "${qn.name}" missing the synthesized "<table>.<column>" shape; cannot emit ALTER`;
  }
  const table = qn.name.slice(0, dot);
  const column = qn.name.slice(dot + 1);
  const schema = schemaOf(qn);
  const tableRef = schema ? `${q(schema)}.${q(table)}` : q(table);
  return `ALTER TABLE ${tableRef} ALTER COLUMN ${q(column)} SET DEFAULT ${newExpr.trim()}`;
}

function emitCheckConstraintAlter(
  qn: QualifiedName,
  newBody: string,
  q: Quoter,
): string {
  // information_schema.CHECK_CONSTRAINTS exposes (CONSTRAINT_SCHEMA,
  // CONSTRAINT_NAME, CHECK_CLAUSE) but NOT the parent table — that lives in
  // INFORMATION_SCHEMA.TABLE_CONSTRAINTS. Resolve the table at runtime via
  // session vars + PREPARE/EXECUTE so the script is self-contained. The
  // electron MySQL driver enables `multipleStatements: true`, so this is
  // delivered to the engine in a single executeQueryV2 call.
  const schema = schemaOf(qn);
  const quotedName = q(qn.name);
  const newBodyTrimmed = newBody.trim();
  const checkSuffix = /^check\s*\(/i.test(newBodyTrimmed)
    ? newBodyTrimmed
    : `CHECK (${newBodyTrimmed})`;
  const schemaFilter = schema ? `AND tc.CONSTRAINT_SCHEMA = ${quoteMysqlText(schema)}` : '';
  const qc = q === quoteAnsi ? '"' : '`';
  // Preserve the SQL body through the outer PREPARE string in either SQL mode.
  // Catalog names use utf8mb3; promote @t before concatenating Unicode bodies.
  return [
    `SET @t = (SELECT CONCAT('${qc}', tc.TABLE_SCHEMA, '${qc}.${qc}', tc.TABLE_NAME, '${qc}') FROM information_schema.TABLE_CONSTRAINTS tc WHERE tc.CONSTRAINT_NAME = ${quoteMysqlText(qn.name)} ${schemaFilter} AND tc.CONSTRAINT_TYPE = 'CHECK' LIMIT 1);`,
    `SET @drop = CONCAT('ALTER TABLE ', CONVERT(@t USING utf8mb4), ${quoteMysqlText(` DROP CHECK ${quotedName}`)});`,
    `PREPARE stmt FROM @drop; EXECUTE stmt; DEALLOCATE PREPARE stmt;`,
    `SET @add = CONCAT('ALTER TABLE ', CONVERT(@t USING utf8mb4), ${quoteMysqlText(` ADD CONSTRAINT ${quotedName} ${checkSuffix}`)});`,
    `PREPARE stmt FROM @add; EXECUTE stmt; DEALLOCATE PREPARE stmt;`,
  ].join('\n');
}

function dispatchBodyAlter(input: EmitObjectBodyAlterInput, q: Quoter): string {
  const { objectKind, objectName, newBody } = input;
  if (objectKind === 'check-constraint') return emitCheckConstraintAlter(objectName, newBody, q);
  if (objectKind === 'default-constraint') return emitDefaultConstraintAlter(objectName, newBody, q);
  if (objectKind === 'view' || objectKind === 'materialized-view') {
    return emitViewBodyAlter(objectName, newBody, q);
  }
  if (objectKind === 'procedure') return emitDropCreateModule(objectName, newBody, q, 'PROCEDURE');
  if (objectKind === 'trigger') return emitDropCreateModule(objectName, newBody, q, 'TRIGGER');
  // inline-tvf / scalar-function / fallback → FUNCTION
  return emitDropCreateModule(objectName, newBody, q, 'FUNCTION');
}

export function emitObjectBodyAlter(
  input: EmitObjectBodyAlterInput,
  options?: MysqlRenameEmitterOptions,
): string {
  return dispatchBodyAlter(input, pickQuoter(options));
}

export type SchemaRenameInput = {
  readonly kind: 'table' | 'view';
  readonly from: QualifiedName;
  readonly to: QualifiedName;
  /** NAV-2 — the renamed view's ORIGINAL bare SELECT body
   *  (`information_schema.VIEWS.VIEW_DEFINITION`), threaded in by the planner
   *  for `kind: 'view'`. Used to recreate the view with its real definition
   *  rather than the data-destroying `SELECT * FROM <old>` placeholder. */
  readonly viewBody?: string;
};

export function emitTableOrViewSchemaAlter(
  input: SchemaRenameInput,
  options?: MysqlRenameEmitterOptions,
): string {
  const q = pickQuoter(options);
  if (input.kind === 'table') {
    return `ALTER TABLE ${quotedQualified(input.from, q)} RENAME TO ${quotedQualified(input.to, q)}`;
  }
  // Views (NAV-2): MySQL has no `RENAME VIEW` / `ALTER VIEW … RENAME` form.
  // The ONLY non-destructive emit recreates the new view from the ORIGINAL
  // view body and then drops the old one. The previous placeholder recreated
  // it as `SELECT * FROM <old>` and then dropped `<old>` — which both broke
  // the new view AND irrecoverably lost the original SELECT body.
  //
  // The planner supplies `viewBody` (the real `VIEW_DEFINITION` SELECT). If —
  // and only if — it is genuinely unavailable, we REFUSE via a typed
  // unrewritable marker the Slice-28 executor declines (`isStubAlterSql`).
  // We NEVER fall back to the data-destroying placeholder.
  const fromRef = quotedQualified(input.from, q);
  const toRef = quotedQualified(input.to, q);
  const body = input.viewBody?.replace(/;\s*$/, '').trim();
  if (!body) {
    return unrewritableRenameSql(
      'mysql',
      `view rename of ${fromRef} requires the original view definition; ` +
        'MySQL has no RENAME VIEW and the body was unavailable',
    );
  }
  return [
    `CREATE OR REPLACE VIEW ${toRef} AS ${body};`,
    `DROP VIEW ${fromRef};`,
  ].join('\n');
}

export type EmitUndoAlterInput =
  | {
      readonly kind: 'body';
      readonly objectKind: ObjectKind;
      readonly objectName: QualifiedName;
      readonly originalBody: string;
    }
  | {
      readonly kind: 'schema';
      readonly schemaRename: SchemaRenameInput;
    };

export function emitUndoAlter(
  input: EmitUndoAlterInput,
  options?: MysqlRenameEmitterOptions,
): string {
  if (input.kind === 'body') {
    return emitObjectBodyAlter(
      {
        objectKind: input.objectKind,
        objectName: input.objectName,
        newBody: input.originalBody,
      },
      options,
    );
  }
  return emitTableOrViewSchemaAlter(
    {
      kind: input.schemaRename.kind,
      from: input.schemaRename.to,
      to: input.schemaRename.from,
    },
    options,
  );
}

export function makeMysqlRenameEmitter(
  options?: MysqlRenameEmitterOptions,
): DialectRenameEmitter {
  return {
    emitObjectBodyAlter: (input) => emitObjectBodyAlter(input, options),
    emitTableOrViewSchemaAlter: (input) => emitTableOrViewSchemaAlter(input, options),
  };
}

export const mysqlRenameEmitter: DialectRenameEmitter = makeMysqlRenameEmitter();
